import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PapiDeviation, PapiExtendedDeviation } from '@wix/da-papi-types';
import { makeInitDeviationXHRCall } from '../../simpleXHR/actionCreators';
import {
  getInitDeviationXHRCallResult,
  getIsLoading,
} from '../../simpleXHR/selectors';
import { SimpleXHREndpoint } from '../../simpleXHR/constants';

/**
 * ( ! ) This should be used carefully - only in single deviation contexts
 */
export const useExtendedDeviation = (
  initialDeviation: PapiDeviation,
  requiredProperty?: keyof PapiExtendedDeviation
) => {
  const isAlreadyExtended = requiredProperty
    ? !!initialDeviation.extended?.[requiredProperty]
    : !!initialDeviation.extended;
  const dispatch = useDispatch();
  const { deviation: extendedDeviation } =
    useSelector(getInitDeviationXHRCallResult) || {};
  const isLoading = useSelector(state =>
    getIsLoading(state, SimpleXHREndpoint.INIT_DEVIATION)
  );

  // Track the last deviation ID we tried to load to prevent duplicate calls
  const lastRequestedDeviationId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (isAlreadyExtended) {
      return;
    }

    const deviationId = initialDeviation.deviationId;

    if (lastRequestedDeviationId.current === deviationId) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (extendedDeviation?.deviationId === deviationId) {
      lastRequestedDeviationId.current = deviationId;
      return;
    }

    lastRequestedDeviationId.current = deviationId;
    dispatch(makeInitDeviationXHRCall(initialDeviation));
  }, [
    dispatch,
    isAlreadyExtended,
    initialDeviation,
    isLoading,
    extendedDeviation,
  ]);

  return isAlreadyExtended
    ? initialDeviation
    : extendedDeviation?.deviationId === initialDeviation.deviationId
      ? // avoid returning extended deviation from previous XHR call
        extendedDeviation
      : undefined;
};

export default useExtendedDeviation;
